import { useMemo, type ReactNode } from 'react';
import { Tabs } from '@viquard/doc-ui/components';
import {
  buildWeek,
  isoDate,
  parseISODate,
} from '../utils/dateUtils.js';

export interface DayStripProps {
  /** ISO date used to derive the week. */
  readonly anchor: string;
  /** ISO date currently selected. */
  readonly selected: string;
  readonly today: string;
  readonly onSelect: (date: string) => void;
  readonly weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined;
  readonly locale?: string | undefined;
  /** Per-day event counts so each pill can render a "has events" dot. */
  readonly eventCounts?: ReadonlyMap<string, number> | undefined;
}

/** Horizontal row of 7 day-pills built on doc-ui's `<Tabs>` primitive
 *  (variant=soft → pill background with a sliding accent indicator).
 *  Each tab's children are a small DOW letter + day number + an
 *  "has-events" dot, layout overridden via the `vq-cal__day-pill` class.
 *
 *  We pick Tabs over a row of raw buttons because it gives us the
 *  WAI-ARIA tablist semantics, arrow-key navigation between pills, and
 *  the moving indicator without re-implementing any of it. */
export function DayStrip(props: DayStripProps): ReactNode {
  const {
    anchor,
    selected,
    today,
    onSelect,
    weekStartsOn = 0,
    locale,
    eventCounts,
  } = props;
  const anchorDate = useMemo(() => parseISODate(anchor), [anchor]);
  const days = useMemo(() => buildWeek(anchorDate, weekStartsOn), [anchorDate, weekStartsOn]);

  return (
    <Tabs
      value={selected}
      onValueChange={onSelect}
      variant="soft"
      size="md"
      activation="manual"
      className="vq-cal__day-strip"
    >
      <Tabs.List aria-label="Day picker">
        {days.map((d) => {
          const key = isoDate(d);
          const isToday = key === today;
          const count = eventCounts?.get(key) ?? 0;
          const dow = new Date(d.year, d.month - 1, d.day).toLocaleDateString(locale, {
            weekday: 'narrow',
          });
          return (
            <Tabs.Tab
              key={key}
              value={key}
              className={['vq-cal__day-pill', isToday ? 'is-today' : ''].filter(Boolean).join(' ')}
            >
              <span className="vq-cal__day-pill-dow">{dow}</span>
              <span className="vq-cal__day-pill-num">{d.day}</span>
              {/* Mark slot is ALWAYS in the DOM so every pill has the same
                * 3-row layout — otherwise pills with no events render as
                * 2-child columns and their day numbers sit lower than
                * neighbours that have the mark. We toggle a data attr so
                * CSS can hide just the visual dot. */}
              <span
                className="vq-cal__day-pill-mark"
                data-has-events={count > 0 ? 'true' : 'false'}
                aria-hidden="true"
              />
            </Tabs.Tab>
          );
        })}
      </Tabs.List>
    </Tabs>
  );
}
